import type { ReactNode } from 'react';
import { ArrowRight, Sparkles, type LucideIcon } from 'lucide-react';
import { AnimatedIcon, type IconMotion } from '@/shared/components/animated-icon';
import { cn } from '@/shared/lib/cn';
import { accentStyle } from '@/features/member-get-member/public/brand';

interface PromoHeroCardProps {
  icon: LucideIcon;
  /** Animação do ícone (CSS, respeita "reduzir movimento"). */
  iconMotion?: IconMotion;
  eyebrow: string;
  title: string;
  /** Texto de apoio; nó para aceitar "Ler mais" e afins. */
  children?: ReactNode;
  /** Com `onClick`, o card inteiro é um botão com a pílula de ação no fim. */
  cta?: string;
  onClick?: () => void;
  /** Cor da loja (`passbook.backgroundColor` / `foregroundColor`). */
  accentColor?: string;
  foregroundColor?: string;
  className?: string;
}

/**
 * Card de destaque das páginas públicas: degradê na cor da loja com brilho
 * (`.mgm-hero`, o mesmo topo da página do Indique e ganhe), ícone animado e
 * texto no par de cor da loja. Usado no topo das telas Indique e ganhe e
 * Pesquisa, e nos atalhos do Início — o card do Início é o mesmo da tela.
 */
export function PromoHeroCard({
  icon,
  iconMotion = 'wiggle',
  eyebrow,
  title,
  children,
  cta,
  onClick,
  accentColor,
  foregroundColor,
  className,
}: PromoHeroCardProps) {
  const body = (
    <>
      {/* brilhos em degradê radial (sem `blur`: no iOS o filtro vaza pelo arredondado) */}
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,rgba(255,255,255,0.22),transparent_42%),radial-gradient(circle_at_15%_115%,rgba(255,255,255,0.14),transparent_45%)]" />

      <div className="relative flex items-start gap-4">
        <span className="relative grid size-14 shrink-0 place-items-center rounded-2xl bg-white/20 shadow-lg ring-1 ring-white/30">
          <AnimatedIcon icon={icon} motion={iconMotion} loop className="size-7" />
          <AnimatedIcon
            icon={Sparkles}
            motion="pulse"
            loop
            className="size-4 opacity-90"
            wrapperClassName="absolute -right-1.5 -top-1.5"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{eyebrow}</p>
          <p className="mt-0.5 text-lg font-bold leading-tight tracking-tight">{title}</p>
          {children ? <div className="mt-1 text-sm leading-snug opacity-90">{children}</div> : null}
        </div>
      </div>

      {cta ? (
        <span className="relative mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/20 px-4 py-2 text-sm font-semibold ring-1 ring-white/25 transition-colors group-hover:bg-white/30">
          {cta}
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      ) : null}
    </>
  );

  const shell = cn(
    'mgm-scope mgm-hero relative flex flex-col overflow-hidden rounded-2xl p-5 text-left shadow-glass-lg',
    className,
  );
  const style = accentStyle({ name: '', backgroundColor: accentColor, foregroundColor });

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={style}
        className={cn(
          shell,
          'group w-full transition-transform hover:scale-[1.01] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
      >
        {body}
      </button>
    );
  }
  return (
    <div style={style} className={shell}>
      {body}
    </div>
  );
}
